import { useEffect, useState } from 'react'
import { Alert, Button, Card, Checkbox, Group, Progress, Select, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import backend from '../../backend/backend'
import { DocumentUploader, QuotePanel } from '../../components/shared/Pieces'
import PageHeader from '../../components/shared/PageHeader'
import { describeError, useAction, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadCustomer, loadCustomers, loadProducts } from '../../store/dataActionCreators'
import { fullName, toKobo } from '../../utils/format'

const STEPS = ['wizard_customer', 'wizard_documents', 'wizard_financing', 'wizard_review']

export default function ApplicationWizard() {
  const t = useT()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const required = useSelector((s) => s.auth.settings.requiredDocuments) || []
  const { busy, run } = useAction()
  const [step, setStep] = useState(0)
  const [customerId, setCustomerId] = useState(params.get('customerId') || null)
  const [search, setSearch] = useState('')
  const [productId, setProductId] = useState(null)
  const [term, setTerm] = useState(null)
  const [price, setPrice] = useState('')
  const [deposit, setDeposit] = useState('')
  const [amount, setAmount] = useState('')
  const [device, setDevice] = useState({ brand: '', model: '', description: '' })
  const [payout, setPayout] = useState({ bank: '', account: '' })
  const [quote, setQuote] = useState(null)
  const [quoteError, setQuoteError] = useState(null)
  const [appId, setAppId] = useState(null)
  const [agreement, setAgreement] = useState(null)
  const [consentName, setConsentName] = useState('')
  const [agreed, setAgreed] = useState(false)

  const customers = useResource(keys.customers(search, 1), () => loadCustomers(search, 1))
  const customer = useResource(customerId ? keys.customer(customerId) : 'customer:none', () => (customerId ? loadCustomer(customerId) : () => Promise.resolve()))
  const products = useResource(keys.products, loadProducts)
  const product = (products.data?.items || []).find((p) => p.id === productId)
  const isCash = product?.kind === 'cash'
  const docs = customer.data?.documents || []
  const missingDocs = required.filter((type) => !docs.some((d) => d.doc_type === type && d.status !== 'rejected'))

  const customerOptions = (customers.data?.items || []).map((c) => ({ value: c.id, label: `${fullName(c)} · ${c.customer_no}` }))
  if (customerId && customer.data?.customer && !customerOptions.some((o) => o.value === customerId)) {
    customerOptions.unshift({ value: customerId, label: `${fullName(customer.data.customer)} · ${customer.data.customer.customer_no}` })
  }

  // Live quote: the backend is the only place the maths is done.
  useEffect(() => {
    setQuote(null)
    setQuoteError(null)
    if (!product || !term) return undefined
    const body = { productId, termMonths: Number(term) }
    if (isCash) body.amountKobo = toKobo(amount)
    else {
      body.priceKobo = toKobo(price)
      body.depositKobo = toKobo(deposit || '0')
    }
    if (Object.values(body).some((v) => Number.isNaN(v))) return undefined
    const timer = setTimeout(() => {
      backend.getQuote(body).then(setQuote).catch((err) => setQuoteError(describeError(t, err)))
    }, 350)
    return () => clearTimeout(timer)
  }, [productId, term, price, deposit, amount, isCash, product, t])

  const saveDraft = async () => {
    const body = {
      customerId, productId, termMonths: Number(term),
      device_brand: device.brand, device_model: device.model, device_description: device.description,
      payout_bank: payout.bank, payout_account: payout.account,
    }
    if (isCash) body.amountKobo = toKobo(amount)
    else {
      body.priceKobo = toKobo(price)
      body.depositKobo = toKobo(deposit || '0')
    }
    const app = await run(() => (appId ? backend.updateApplication(appId, body) : backend.createApplication(body)))
    if (!app) return
    setAppId(app.id)
    const agr = await run(() => backend.getAgreement(app.id))
    if (agr) {
      setAgreement(agr)
      setStep(3)
    }
  }

  const finish = async () => {
    const consent = await run(() => backend.applicationAction(appId, 'consents', { accepted_name: consentName.trim() }))
    if (!consent) return
    const done = await run(() => backend.applicationAction(appId, 'submit', {}), { success: 'application_submitted' })
    if (done) navigate(`/agent/applications/${appId}`)
  }

  const canNext = [!!customerId, missingDocs.length === 0, !!quote, agreed && consentName.trim().length > 1][step]

  return (
    <>
      <PageHeader title="new_application" />
      <Stack maw={720} gap="md">
        <div>
          <Text size="sm" c="dimmed" mb={4}>
            <Message id="wizard_step" values={{ n: step + 1, total: STEPS.length }} /> · <Message id={STEPS[step]} />
          </Text>
          <Progress value={((step + 1) / STEPS.length) * 100} />
        </div>

        {step === 0 && (
          <Stack>
            <Select
              label={t('wizard_pick_customer')}
              searchable
              data={customerOptions}
              value={customerId}
              onChange={setCustomerId}
              onSearchChange={setSearch}
              filter={({ options }) => options}
              nothingFoundMessage={t('customers_empty')}
            />
            <Text size="sm">
              <Message id="wizard_or" />{' '}
              <Text component={Link} to="/agent/customers/new" c="brand" span>
                <Message id="new_customer" />
              </Text>
            </Text>
          </Stack>
        )}

        {step === 1 && customerId && (
          <Stack>
            {['government_id', 'customer_photo', 'proof_of_address'].map((type) => (
              <DocumentUploader key={type} customerId={customerId} docType={type} documents={docs} onChange={customer.reload} />
            ))}
            {missingDocs.length > 0 && (
              <Alert color="orange">
                <Message id="wizard_docs_missing" />: {missingDocs.map((d) => t(`doc_${d}`)).join(', ')}
              </Alert>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack>
            <Select
              label={t('field_product')}
              data={(products.data?.items || []).map((p) => ({ value: p.id, label: p.name }))}
              value={productId}
              onChange={(v) => { setProductId(v); setTerm(null) }}
              nothingFoundMessage={t('products_none_active')}
            />
            {products.data && products.data.items.length === 0 && (
              <Alert color="orange"><Message id="products_none_active" /></Alert>
            )}
            {product && (
              <>
                <Select label={t('field_term')} data={product.allowed_terms.map((n) => ({ value: String(n), label: t('term_months', { count: n }) }))} value={term} onChange={setTerm} />
                {isCash ? (
                  <>
                    <TextInput label={t('field_amount')} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.currentTarget.value)} />
                    <TextInput label={t('field_payout_bank')} value={payout.bank} onChange={(e) => setPayout({ ...payout, bank: e.currentTarget.value })} />
                    <TextInput label={t('field_payout_account')} inputMode="numeric" value={payout.account} onChange={(e) => setPayout({ ...payout, account: e.currentTarget.value })} />
                  </>
                ) : (
                  <>
                    <TextInput label={t('field_device_brand')} value={device.brand} onChange={(e) => setDevice({ ...device, brand: e.currentTarget.value })} />
                    <TextInput label={t('field_device_model')} value={device.model} onChange={(e) => setDevice({ ...device, model: e.currentTarget.value })} />
                    <TextInput label={t('field_price')} inputMode="decimal" value={price} onChange={(e) => setPrice(e.currentTarget.value)} />
                    <TextInput label={t('field_deposit')} inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.currentTarget.value)}
                      description={product.min_deposit_pct !== null ? t('deposit_min_hint', { pct: product.min_deposit_pct }) : undefined} />
                  </>
                )}
                <QuotePanel quote={quote} error={quoteError} />
              </>
            )}
          </Stack>
        )}

        {step === 3 && agreement && (
          <Stack>
            <QuotePanel quote={quote} />
            <Card>
              <Title order={5} mb="xs"><Message id="agreement_title" /></Title>
              <Textarea value={agreement.text} readOnly autosize minRows={6} maxRows={14} />
            </Card>
            <TextInput label={t('consent_name')} value={consentName} onChange={(e) => setConsentName(e.currentTarget.value)} />
            <Checkbox label={t('consent_checkbox')} checked={agreed} onChange={(e) => setAgreed(e.currentTarget.checked)} />
          </Stack>
        )}

        <Group justify="space-between">
          <Button variant="default" disabled={step === 0 || busy} onClick={() => setStep(step === 3 ? 2 : step - 1)}>
            <Message id="back" />
          </Button>
          {step < 2 && (
            <Button disabled={!canNext} onClick={() => setStep(step + 1)}>
              <Message id="next" />
            </Button>
          )}
          {step === 2 && (
            <Button disabled={!canNext} loading={busy} onClick={saveDraft}>
              <Message id="next" />
            </Button>
          )}
          {step === 3 && (
            <Button disabled={!canNext} loading={busy} onClick={finish}>
              <Message id="consent_and_submit" />
            </Button>
          )}
        </Group>
      </Stack>
    </>
  )
}
